import { useCallback, useMemo, useState } from 'react'
import { createContainer } from 'unstated-next'

import { reportClientError } from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { usePortal } from '../../../app/_providers/PortalProvider.client'
import { shortenShopifyIdToNumeric } from '../../../app/_utils/shortenShopifyId'
import { generateLineItemProperties } from '../../../containers/EmbroideryService/utils'
import { CartContainer, UseCartNewCartItem } from '../cart'
import { useMakeCartItem } from '../cart/utils'

import type { Open, QuickBuyData, State } from './types'
import type { EmbroideryConfig, EmbroideryVariant, Variant } from '../../../types/graphql'

function useQuickBuy({
  defaultGarmentProperties,
  defaultEmbroideryProperties,
}: {
  /** Additional line item properties that should be included on garments added through quick buy */
  defaultGarmentProperties?: Record<string, string>
  /** Additional line item properties that should be included on embroidery items added through quick buy */
  defaultEmbroideryProperties?: Record<string, string>
} = {}) {
  const { addItems } = CartContainer.useContainer()
  const makeCartItem = useMakeCartItem()
  const { portal } = usePortal()

  const [state, setState] = useState<State>({ modalState: 'Unopened' })

  const openModal = useCallback(
    (data: QuickBuyData) => {
      setState({ modalState: 'Open', ...data })
    },
    [setState]
  )

  const closeModal = useCallback(() => {
    setState(current => ({ ...(current as Open), modalState: 'Closed' }))
  }, [setState])

  const addToCart = useCallback(
    ({
      embroideryConfig,
      embroideryVariant,
      finalSale,
      variant,
      quantity,
    }: {
      embroideryConfig?: EmbroideryConfig
      embroideryVariant?: EmbroideryVariant
      finalSale?: boolean
      variant: Variant
      swPromoEligible?: boolean
      quantity?: number
    }) => {
      const getItems = (): UseCartNewCartItem[] => {
        const shopifyId = shortenShopifyIdToNumeric(variant.shopifyId)
        if (!embroideryVariant || !shopifyId) {
          return makeCartItem({
            finalSale: finalSale ?? variant.product?.finalSale ?? false,
            item: variant,
            options: {
              quantity,
              attributedPortal: undefined,
              properties: defaultGarmentProperties,
            },
          })
        }

        // Line item properties for the embroidery item
        const embroideryProperties = {
          ...defaultEmbroideryProperties,
          ...generateLineItemProperties(
            {
              embroideryItemVariant: { ...embroideryVariant, images: [] },
              logoName: embroideryConfig?.logoName,
              logoImageSource: embroideryConfig?.image,
            },
            undefined,
            shopifyId
          ),
        }

        // Line item properties for the product
        const garmentProperties = {
          ...defaultGarmentProperties,
          _embroideryId: variant.shopifyId,
          _guid: embroideryProperties._guid,
          _LPROP: embroideryProperties._LPROP,
        }

        return [
          ...makeCartItem({
            finalSale: true,
            item: embroideryVariant,
            options: {
              quantity,
              properties: embroideryProperties,
              attributedPortal: portal?.name
                ? {
                    name: portal.name,
                    itemType: 'PortalLogo',
                  }
                : undefined,
            },
          }),
          ...makeCartItem({
            finalSale: true,
            item: variant,
            options: {
              quantity,
              properties: garmentProperties,
              attributedPortal: portal?.name
                ? {
                    name: portal.name,
                    itemType: 'PortalColor',
                  }
                : undefined,
            },
          }),
        ]
      }

      try {
        addItems(getItems())
      } catch (error) {
        reportClientError({
          error,
          context: {
            scope: 'QuickBuy Add to Bag',
          },
        })

        throw error
      }
    },
    [defaultEmbroideryProperties, defaultGarmentProperties, makeCartItem, portal?.name, addItems]
  )

  return useMemo(
    () => ({
      addToCart,
      closeModal,
      openModal,
      state,
    }),
    [addToCart, closeModal, openModal, state]
  )
}
/**
 * @deprecated Use QuickbuyDialog.client.tsx from app directory which is based on newer apis.
 */
export const QuickBuyContainer = createContainer(useQuickBuy)
